import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '');

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  material: string;
  size: string;
  stock_quantity: number;
}

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  product: Product;
}

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  totalAmount: number;
  onSuccess: () => void;
}

const CheckoutForm = ({ items, totalAmount, onSuccess, onClose }: {
  items: CartItem[];
  totalAmount: number;
  onSuccess: () => void;
  onClose: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [shippingAddress, setShippingAddress] = useState('');
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    if (!shippingAddress.trim()) {
      toast({
        title: "请填写收货地址",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "请先登录",
          variant: "destructive",
        });
        return;
      }

      // Create payment intent
      const { data, error } = await supabase.functions.invoke('create-payment-intent', {
        body: {
          items: items.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          })),
          shipping_address: shippingAddress
        }
      });

      if (error) {
        throw new Error(error.message || '创建支付失败');
      }

      // Confirm payment with Stripe
      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error('支付表单未正确加载');
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        data.client_secret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              email: session.user.email,
            }
          }
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message || '支付失败');
      }

      if (paymentIntent.status === 'succeeded') {
        // Confirm payment on backend
        const { error: confirmError } = await supabase.functions.invoke('confirm-payment', {
          body: {
            payment_intent_id: paymentIntent.id
          }
        });

        if (confirmError) {
          console.error('Payment confirmation error:', confirmError);
          // Payment succeeded but confirmation failed - this is handled on backend
        }

        toast({
          title: "支付成功",
          description: "您的订单已完成，感谢您的购买！",
        });
        
        onSuccess();
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      toast({
        title: "支付失败",
        description: error.message || '支付过程中出现错误',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <h3 className="font-semibold">订单详情</h3>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {items.map((item) => (
            <div key={item.id} className="flex justify-between items-center text-sm">
              <span className="flex-1 truncate">
                {item.product.name} × {item.quantity}
              </span>
              <span className="font-medium">
                ¥{(item.product.price * item.quantity).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
        <Separator />
        <div className="flex justify-between items-center font-semibold">
          <span>总计:</span>
          <span className="text-lg text-primary">¥{totalAmount.toFixed(2)}</span>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="shipping-address">收货地址 *</Label>
        <Textarea
          id="shipping-address"
          value={shippingAddress}
          onChange={(e) => setShippingAddress(e.target.value)}
          placeholder="请输入详细的收货地址"
          required
        />
      </div>

      <div className="space-y-2">
        <Label>支付信息</Label>
        <div className="border rounded-md p-3">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
            }}
          />
        </div>
      </div>

      <div className="flex space-x-3">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={loading}
          className="flex-1"
        >
          取消
        </Button>
        <Button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1"
        >
          {loading ? '处理中...' : `支付 ¥${totalAmount.toFixed(2)}`}
        </Button>
      </div>
    </form>
  );
};

export const CheckoutModal = ({ isOpen, onClose, items, totalAmount, onSuccess }: CheckoutModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>结账</DialogTitle>
        </DialogHeader>

        <Elements stripe={stripePromise}>
          <CheckoutForm
            items={items}
            totalAmount={totalAmount}
            onSuccess={onSuccess}
            onClose={onClose}
          />
        </Elements>
      </DialogContent>
    </Dialog>
  );
};